"use client";

import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import Image from "next/image";
import { ArrowRight, ChevronDown, Play, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { track } from "@/components/analytics/track";
import { LoopBeats } from "@/components/LoopBeats";

const SparklineChart = dynamic(() =>
  import("./SparklineChart").then((m) => ({ default: m.SparklineChart })),
  { ssr: false }
);

/**
 * Hero — two-line pitch, then the closed-loop beats and CTAs.
 */
export function Hero({ basicMonthlyLabel }: { basicMonthlyLabel: string }) {
  const t = useTranslations("Home");
  const tc = useTranslations("Common");

  return (
    <section className="relative flex min-h-[min(100svh,980px)] w-full flex-col items-stretch justify-center overflow-x-hidden overflow-y-visible quant-grid px-4 py-28 sm:px-6 md:py-36">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
      >
        <div className="absolute left-1/2 top-[42%] h-[22rem] w-[22rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-teal-primary/30 blur-[110px] sm:h-[40rem] sm:w-[40rem]" />
        <div className="absolute right-[6%] top-[16%] hidden h-64 w-64 rounded-full bg-maroon/20 blur-[100px] sm:block" />
      </div>
      <div className="pointer-events-none absolute inset-0 opacity-40 sm:opacity-[0.72]">
        <SparklineChart />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0d1412]/55 via-transparent to-[#0d1412]" />

      <div className="relative z-10 mx-auto w-full min-w-0 max-w-5xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-7 flex flex-col items-center gap-4 sm:mb-8 sm:gap-5"
        >
          <Image
            src="/atabet_logo.jpg"
            alt={tc("logoAlt")}
            width={72}
            height={72}
            className="hidden rounded-xl object-contain drop-shadow-lg sm:block"
            priority
          />
          <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-teal-primary/40 bg-slate-950/55 px-3 py-1.5 backdrop-blur-sm sm:px-4 sm:py-2">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-teal-light sm:h-4 sm:w-4" />
            <span className="min-w-0 text-center text-xs font-medium leading-snug text-pretty text-slate-300 sm:text-sm">
              {t("heroBadge")}
            </span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mb-5 px-1 text-[clamp(1.55rem,6.2vw,4.5rem)] font-bold leading-[1.12] tracking-tight text-pretty text-slate-100"
        >
          <span className="block">{t("heroTitle")}</span>
          <span className="text-glow-teal block text-teal-light">
            {t("heroAccent")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.14 }}
          className="mb-5 px-1 text-base font-medium text-pretty text-slate-200 sm:text-xl md:text-2xl"
        >
          <span className="block text-slate-400 sm:inline">
            {t("heroContrastLead")}
          </span>{" "}
          <span className="block text-teal-light sm:inline">
            {t("heroContrastTail")}
          </span>
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.18 }}
          className="mx-auto mb-8 w-full max-w-[20.5rem] px-2 text-base leading-relaxed text-slate-400 sm:max-w-xl md:text-lg"
        >
          {t("heroSubtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="mb-10"
        >
          <LoopBeats />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.28 }}
          className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
        >
          <Link
            href="/product"
            onClick={() =>
              track("cta_click", { meta: { label: "hero_download" } })
            }
            className="group flex w-full items-center justify-center gap-2 rounded-lg bg-maroon px-8 py-4 font-semibold text-white shadow-lg shadow-maroon/25 transition-all duration-200 hover:bg-maroon-hover sm:w-auto"
          >
            {t("downloadCta")}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            href="/#product-demo"
            onClick={() =>
              track("cta_click", { meta: { label: "hero_watch_demo" } })
            }
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-teal-light/50 bg-teal-primary/20 px-8 py-4 font-medium text-teal-light transition-colors hover:border-teal-light hover:bg-teal-primary/30 hover:text-white sm:w-auto"
          >
            <Play className="h-4 w-4 fill-current" />
            {t("watchDemo")}
          </Link>
        </motion.div>
        <p className="mx-auto mt-6 w-full max-w-[20.5rem] px-2 text-sm leading-relaxed text-slate-500 sm:max-w-xl">
          {t("heroTrialNote", { price: basicMonthlyLabel })}
        </p>
      </div>

      <Link
        href="/#product-demo"
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 text-slate-500 transition-colors hover:text-teal-light md:inline-flex"
        aria-label={t("watchDemo")}
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </Link>
    </section>
  );
}
